import { type CardDispute, CardDisputeWorkflow } from '@column/api';

export type DisputeStage = CardDispute['stage'];

export interface LifecycleEdge {
  actor: string;
  description: string;
}

export type LifecycleTopology = Partial<Record<DisputeStage, Partial<Record<DisputeStage, LifecycleEdge>>>>;

const initiated = {
  disputed: { actor: 'Customer / issuer → Card Network', description: 'Submit; network accepts' },
  dispute_won: { actor: 'Card Network / platform', description: 'Rapid resolution or platform accepts claim' },
  dispute_close: { actor: 'Card Network / customer', description: 'Network declines or local closure' },
};

const disputedEndings = {
  dispute_won: { actor: 'Acquirer / merchant', description: 'Accepts the dispute' },
  dispute_close: { actor: 'Card Network / customer', description: 'Network refusal or recall' },
};

const arbitration = {
  dispute_won: { actor: 'Card Network', description: 'Rules in issuer favor' },
  dispute_close: { actor: 'Card Network', description: 'Rules in acquirer favor' },
};

// Ordinary transitions mirror the Go maps, pinned by topology.test.ts to their shared golden file.
// Sync may record other observations; neither these edges nor a date authorizes a customer action.
export const DISPUTE_TOPOLOGIES: Record<CardDisputeWorkflow, LifecycleTopology> = {
  [CardDisputeWorkflow.COLLABORATION]: {
    initiated,
    disputed: {
      ...disputedEndings,
      merchant_responded: { actor: 'Acquirer / merchant', description: 'Responds to the dispute' },
    },
    merchant_responded: {
      merchant_response_recalled: { actor: 'Acquirer / merchant', description: 'Recalls its response' },
      dispute_close: { actor: 'Customer / issuer', description: 'Accepts merchant response' },
      prearbitration: { actor: 'Customer / issuer', description: 'Continues to pre-arbitration' },
    },
    prearbitration: {
      dispute_won: { actor: 'Acquirer / merchant · Card Network', description: 'Accepts or response window expires' },
      prearbitration_responded: { actor: 'Acquirer / merchant', description: 'Answers pre-arbitration' },
    },
    prearbitration_responded: {
      dispute_close: { actor: 'Customer / issuer', description: 'Accepts acquirer answer' },
      arbitration: { actor: 'Issuer → Column operations', description: 'Requests arbitration filing' },
    },
    arbitration,
    dispute_won: {},
    dispute_close: {},
  },
  [CardDisputeWorkflow.ALLOCATION]: {
    initiated,
    disputed: {
      ...disputedEndings,
      prearbitration: { actor: 'Acquirer / merchant', description: 'Contests fraud or authorization' },
    },
    prearbitration: {
      dispute_close: { actor: 'Customer / issuer · Card Network', description: 'Full / partial acceptance or expiry' },
      prearbitration_responded: { actor: 'Customer / issuer', description: 'Declines, if permitted' },
    },
    prearbitration_responded: {
      dispute_won: { actor: 'Acquirer / merchant', description: 'Accepts issuer response' },
      arbitration: { actor: 'Acquirer / merchant', description: 'Chooses to file arbitration' },
    },
    arbitration,
    dispute_won: {},
    dispute_close: {},
  },
};

export const LIFECYCLE_STAGE_ORDER: Record<CardDisputeWorkflow, DisputeStage[]> = {
  [CardDisputeWorkflow.COLLABORATION]: [
    'initiated',
    'disputed',
    'merchant_responded',
    'merchant_response_recalled',
    'prearbitration',
    'prearbitration_responded',
    'arbitration',
    'dispute_won',
    'dispute_close',
  ],
  [CardDisputeWorkflow.ALLOCATION]: [
    'initiated',
    'disputed',
    'prearbitration',
    'prearbitration_responded',
    'arbitration',
    'dispute_won',
    'dispute_close',
  ],
};

export const isLifecycleWorkflow = (workflow: unknown): workflow is CardDisputeWorkflow =>
  workflow === CardDisputeWorkflow.COLLABORATION || workflow === CardDisputeWorkflow.ALLOCATION;

export const getLifecycleEdges = (topology: LifecycleTopology) =>
  Object.entries(topology).flatMap(([from, targets]) =>
    Object.entries(targets).map(([to, edge]) => ({ from: from as DisputeStage, to: to as DisputeStage, ...edge }))
  );
